/**
 * Legal consent error code -> Polish message mapper.
 *
 * Mirrors the pattern of components/admin/tenant-deletion/error-code-mapper.ts,
 * but for the legal contract whose error body is `{ code }` directly on
 * `response.data` (not nested under `error`).
 *
 * Contract (spec docs/specifications/2026-07-03-legal-consent-soft-modal/spec.md):
 * - 400 { code: 'LEGAL_VERSION_MISMATCH' }   (version drifted between /auth/me and accept)
 * - 400 { code: 'DUPLICATE_DOCUMENT_TYPE' }  (same documentType submitted twice in one request)
 *
 * `LEGAL_REACCEPTANCE_REQUIRED` (403 gate) is gone — the legal gate on grants
 * data endpoints was removed (soft modal replaces the hard gate); no endpoint
 * returns it anymore.
 */
export const LEGAL_VERSION_MISMATCH = "LEGAL_VERSION_MISMATCH";
export const DUPLICATE_DOCUMENT_TYPE = "DUPLICATE_DOCUMENT_TYPE";

export type LegalErrorCode =
  | typeof LEGAL_VERSION_MISMATCH
  | typeof DUPLICATE_DOCUMENT_TYPE;

const ERROR_CODE_MESSAGES: Record<LegalErrorCode, string> = {
  LEGAL_VERSION_MISMATCH:
    "Dokumenty zostały w międzyczasie zaktualizowane. Odświeżamy treść — zapoznaj się i zaakceptuj ponownie.",
  DUPLICATE_DOCUMENT_TYPE:
    "Wystąpił błąd w przesłanych danych. Odśwież stronę i spróbuj ponownie.",
};

const DEFAULT_FALLBACK =
  "Nie udało się zapisać akceptacji. Spróbuj ponownie za chwilę.";

/**
 * Map a backend legal error code to a Polish user-facing message.
 * Returns a default fallback for unknown/absent codes.
 */
export function mapLegalErrorCode(
  code: LegalErrorCode | string | undefined | null,
): string {
  if (!code) return DEFAULT_FALLBACK;
  return ERROR_CODE_MESSAGES[code as LegalErrorCode] ?? DEFAULT_FALLBACK;
}

/**
 * Type guard for the legal error shape: `{ response: { data: { code: unknown } } }`.
 */
function isLegalAxiosError(
  e: unknown,
): e is { response: { status?: number; data: { code?: unknown } } } {
  if (typeof e !== "object" || e === null) return false;
  const r = (e as { response?: unknown }).response;
  if (typeof r !== "object" || r === null) return false;
  const d = (r as { data?: unknown }).data;
  return typeof d === "object" && d !== null;
}

/**
 * Extract the legal error code from an axios-style error (`response.data.code`).
 * Returns undefined if the shape doesn't match.
 */
export function extractLegalErrorCode(error: unknown): string | undefined {
  if (!isLegalAxiosError(error)) return undefined;
  const code = error.response.data.code;
  return typeof code === "string" ? code : undefined;
}

/**
 * True when the error represents a network failure (no HTTP response received).
 */
export function isNetworkError(error: unknown): boolean {
  if (isLegalAxiosError(error)) return false;
  if (typeof error !== "object" || error === null) return false;
  const e = error as { request?: unknown; message?: unknown };
  if (e.request) return true;
  return (
    typeof e.message === "string" &&
    (e.message.includes("Network") || e.message.includes("timeout"))
  );
}
